import { Button, Row } from 'antd';
import React, { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import apiPaths from 'services/apiPaths';
import { ExperienceDetailWrapper } from 'src/components/challenge/ExperienceDetailWrapper';
import CustomModal from 'src/components/CustomModal';
import { IRootReducers } from 'src/reducers';
import { ButtonStatusEnum, ExperienceDetailStatusEnum } from 'src/shared/enums';
import {
  ChallengeDetail,
  IChallengeResourceRender,
  IExperienceActivator,
  IExperienceDetailWrapper,
  IUser,
} from 'src/shared/models';
import { completeChallengeUserResource, dispatchCustomEvent } from 'utils';

interface OwnProps extends IChallengeResourceRender {
  config: IExperienceActivator;
}

interface StateProps {
  challenge: ChallengeDetail;
  user: IUser;
}

type Props = StateProps & OwnProps;

const ExperienceActivator: FC<Props> = ({
  challenge,
  config,
  user,
  updateChallenge,
  updateUserPoints,
}) => {
  const { formatMessage } = useIntl();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const {
    buttonLabel,
    description,
    linkLabel,
    linkUrl,
    modalButtonLabel,
    modalDescription,
    title,
  } = config;

  const performAction = async () => {
    try {
      setLoading(true);
      const err = await completeChallengeUserResource({
        dataPath: apiPaths.CHALLENGES.COMPLETE_EXPERIENCE,
        idResource: config.idResource,
        updateChallenge,
        updateUserPoints,
      });

      if (!err) return handleCloseModal();

      return setError(
        formatMessage({ id: 'page.experience.error-activation' })
      );
    } catch (err) {
      console.error(err);
      setError(formatMessage({ id: 'page.experience.error-activation' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
    setLoading(false);
  };

  const handleOpenModal = () => {
    dispatchCustomEvent({
      eventCategory: `experience-${challenge.challengeId.toString()}`,
      eventAction: 'activate',
      eventLabel: 'legal',
    });

    setShowModal(true);
  };

  const handleClickLink = () => {
    const isValidLink =
      linkUrl && (linkUrl.includes('http://') || linkUrl.includes('https://'));
    if (!isValidLink) return;

    const a = document.createElement('a');
    a.href = linkUrl;
    a.setAttribute('target', '_blank');
    a.click();
  };

  const wrapperConfig: IExperienceDetailWrapper = {
    title,
    status: ExperienceDetailStatusEnum.IN_PROGRESS,
    button: {
      label: buttonLabel,
      status: ButtonStatusEnum.ENABLED,
      performAction: handleOpenModal,
    },
    loading,
  };

  if (config.isCompleted) {
    wrapperConfig.button = {
      label: formatMessage({
        id: 'experience.button-completed',
      }),
      performAction: null,
      status: ButtonStatusEnum.DONE,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DONE;
  }

  if (config.isBlocked) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.DISABLED,
      performAction: null,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DISABLED;
  }

  const detailButton = linkLabel && (
    <span
      className="experience-detail__button experience-detail__button--link experience-detail__button--enabled"
      onClick={handleClickLink}
    >
      <span className="experience-detail__button--content experience-detail__link-button experience-detail__button--content-enabled">
        {linkLabel}
      </span>
    </span>
  );

  const modalContent = (
    <Row justify="center">
      <Row justify="center" className="block">
        <span className="experience-modal__text">{modalDescription}</span>
      </Row>
      <Row justify="center">
        <Button
          className="btn-primary btn-primary--black"
          onClick={performAction}
        >
          {modalButtonLabel}
        </Button>
      </Row>
    </Row>
  );

  const modalConfig = {
    content: modalContent,
    hideFooter: true,
    visible: showModal,
    onCancel: handleCloseModal,
    showIcon: false,
    error,
    loading,
  };

  return (
    <ExperienceDetailWrapper {...wrapperConfig}>
      <>
        <div className="experience-detail__activation-description">
          {description}
          {detailButton}
        </div>
      </>
      <CustomModal {...modalConfig} />
    </ExperienceDetailWrapper>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    user: state.auth.user,
  };
};

export default connect(mapStateToProps)(ExperienceActivator);
